import React, { useEffect, useState } from "react";
import { Package, RefreshCcw, Plus, MapPin, Trash2, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { OrderCard } from "../components/OrderCard";
import { CustomHeader } from "../components/AuthHeader";
import { OrderDetailsModal } from "../components/OrderDetailsModal";
import { useOrderStore } from "../features/orders/orderStore";
import { Order } from "../features/orders/types";
import { CustomButton } from "../components/CustomButton";

export default function TrackOrderScreen() {
  const { orders, isLoading, error, fetchCustomerOrdersByStatuses } = useOrderStore();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const navigate = useNavigate();

  const loadOrders = () => {
    fetchCustomerOrdersByStatuses([
      "open",
      "collected",
      "received",
      "ready_to_pickup",
      "ready_for_delivery",
      "processing",
    ]);
  };

  useEffect(() => {
    loadOrders();
  }, [fetchCustomerOrdersByStatuses]);

  const handleOrderTap = (order: Order) => {
    setSelectedOrder(order);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Premium Header */}
      <div className="bg-white px-6 pt-8 pb-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-b-[40px] mb-8">
        <div className="flex items-center justify-between mb-2">
          <CustomHeader
            title="Track Order"
            subtitle="Real-time updates on your laundry"
          />
          <button 
            onClick={loadOrders}
            className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${isLoading ? 'animate-spin' : ''}`}
          >
            <RefreshCcw size={18} className="text-gray-400" />
          </button>
        </div>
      </div>

      <div className="px-6">
        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 w-full bg-white rounded-3xl animate-pulse flex items-center p-6 gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 bg-gray-100 rounded w-1/3" />
                  <div className="h-3 bg-gray-50 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="bg-red-50 border border-red-100 rounded-[24px] p-6 text-center">
            <p className="text-sm text-red-600 font-medium">{error}</p>
            <button onClick={loadOrders} className="text-xs text-red-400 underline mt-2">Try again</button>
          </div>
        )}

        {/* Empty State - Triggered if no orders exist */}
        {!isLoading && !error && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 animate-in fade-in zoom-in duration-300">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-[#44B6CE] blur-2xl opacity-10 rounded-full" />
              <div className="relative bg-white shadow-xl shadow-cyan-100/50 rounded-full p-8">
                <Package className="h-10 w-10 text-[#44B6CE]" />
              </div>
            </div>
            <div className="text-center px-8">
              <h3 className="text-xl font-bold text-gray-900">All caught up!</h3>
              <p className="text-gray-500 mt-2 mb-8 leading-relaxed">
                You don't have any active orders right now.
              </p>
              <CustomButton
                variant="primary"
                onClick={() => navigate("/order/new")}
                className="px-8 rounded-full flex items-center gap-2 active:scale-95 transition-transform"
              >
                <Plus size={18} />
                Create New Order
              </CustomButton>
            </div>
          </div>
        )}

        {/* Orders List - Sorted newest first */}
        {!isLoading && !error && orders.length > 0 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between mb-4 px-2">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.15em]">
                {orders.length} Active {orders.length === 1 ? "Order" : "Orders"}
              </p>
              <div className="h-1 w-1 rounded-full bg-gray-300" />
            </div>
            
            <div className="flex flex-col gap-4">
              {[...orders]
                .sort((a, b) => {
                  // Primary sort by date, secondary sort by ID to break ties
                  const dateDiff = new Date(b.received_date).getTime() - new Date(a.received_date).getTime();
                  return dateDiff !== 0 ? dateDiff : Number(b.id) - Number(a.id);
                })
                .map((order) => (
                  <div 
                    key={order.id} 
                    className="transform transition-transform active:scale-[0.98]"
                  >
                    <OrderCard
                      orderId={order.order_no}
                      status={order.status}
                      dateTime={order.received_date}
                      onClick={() => handleOrderTap(order)}
                      onTrackOrder={() => handleOrderTap(order)}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <OrderDetailsModal
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder as Order}
      />
    </div>
  );
}